// src/admin/pages/UserDetailPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, ShieldOff, KeyRound, Mail, Trash2, UserPlus } from 'lucide-react';
import AdminTable from '../components/AdminTable.jsx';
import ConfirmDialog from '../components/ConfirmDialog.jsx';
import ResetPasswordModal from './ResetPasswordModal.jsx';
import AssignTenantModal from './AssignTenantModal.jsx';
import {
  fetchUser, updateUserEmail, activateUser, deactivateUser,
  grantSuperadmin, revokeSuperadmin,
  fetchUserTenants, removeUserFromTenant, changeUserRole,
  adminListTenants,
} from '../adminApi.js';

const cardStyle = {
  background: '#ffffff', borderRadius: '10px',
  border: '1px solid rgba(0,0,0,0.06)', padding: '24px',
};

const btnStyle = (color = '#3a3a3c') => ({
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '6px 12px', borderRadius: '6px',
  border: '1px solid rgba(0,0,0,0.1)', background: '#ffffff',
  color, fontSize: '12px', cursor: 'pointer',
  fontFamily: "'DM Sans', sans-serif",
});

export default function UserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [userTenants, setUserTenants] = useState([]);
  const [allTenants, setAllTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    adminListTenants().then(setAllTenants).catch(() => setAllTenants([]));
  }, []);

  // Modal states
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [showResetPw, setShowResetPw] = useState(false);
  const [showAssignTenant, setShowAssignTenant] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const loadUser = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [userData, tenantData] = await Promise.all([
        fetchUser(userId),
        fetchUserTenants(userId),
      ]);
      setUser(userData);
      setUserTenants(tenantData);
      setNewEmail(userData.email);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadUser(); }, [loadUser]);

  async function handleEmailSave() {
    try {
      await updateUserEmail(userId, newEmail);
      setEditingEmail(false);
      loadUser();
    } catch (err) { alert(err.message); }
  }

  // The three handlers below are wrapped in ConfirmDialog's onConfirm.
  // The dialog awaits them and surfaces any rejection as an inline error,
  // so they no longer need a local try/catch+alert. loadUser() side-effects
  // still run on success; if they throw, the dialog catches and displays.
  async function handleToggleActive() {
    if (user.is_active) await deactivateUser(userId);
    else await activateUser(userId);
    loadUser();
  }

  async function handleToggleSuperadmin() {
    if (user.is_superadmin) await revokeSuperadmin(userId);
    else await grantSuperadmin(userId);
    loadUser();
  }

  async function handleRemoveFromTenant(tenantId) {
    await removeUserFromTenant(tenantId, userId);
    loadUser();
  }

  async function handleChangeRole(tenantId, newRole) {
    try {
      await changeUserRole(tenantId, userId, newRole);
      loadUser();
    } catch (err) { alert(err.message); }
  }

  if (loading) return <div style={{ padding: '40px', color: '#6c6c6d', fontFamily: "'DM Sans', sans-serif" }}>Caricamento...</div>;
  if (error) return <div style={{ padding: '40px', color: '#EF4444', fontFamily: "'DM Sans', sans-serif" }}>{error}</div>;
  if (!user) return null;

  // userTenants from fetchUserTenants() returns the JWT-shape {tenant_id, ...}.
  // allTenants from adminListTenants() returns the new {id, ...} shape.
  const assignedTenantIds = new Set(userTenants.map(t => t.tenant_id));
  const availableTenants = allTenants.filter(t => !assignedTenantIds.has(t.id));

  const tenantColumns = [
    { key: 'display_name', label: 'Tenant' },
    {
      key: 'role', label: 'Ruolo', width: '140px',
      render: (row) => (
        <select
          value={row.role}
          onChange={e => handleChangeRole(row.tenant_id, e.target.value)}
          style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid rgba(0,0,0,0.1)', fontSize: '12px', fontFamily: "'DM Sans', sans-serif" }}
        >
          {['viewer', 'editor', 'admin'].map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      ),
    },
    {
      key: 'actions', label: '', width: '80px',
      render: (row) => (
        <button
          onClick={() => setConfirmAction({
            title: 'Rimuovi da tenant',
            message: `Rimuovere ${user.email} da ${row.display_name}? L'accesso sarà revocato al prossimo token refresh.`,
            onConfirm: () => handleRemoveFromTenant(row.tenant_id),
            variant: 'danger',
          })}
          style={btnStyle('#EF4444')}
        >
          <Trash2 size={13} /> Rimuovi
        </button>
      ),
    },
  ];

  return (
    <div style={{ padding: '32px 40px', fontFamily: "'DM Sans', sans-serif", color: '#1d1d1f', maxWidth: '900px' }}>
      <button onClick={() => navigate('/admin/users')} style={{ ...btnStyle(), marginBottom: '20px', background: 'transparent', border: 'none' }}>
        <ArrowLeft size={15} /> Torna alla lista
      </button>

      <div style={{ ...cardStyle, marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            {editingEmail ? (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid rgba(0,0,0,0.12)', fontSize: '16px', fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}
                />
                <button onClick={handleEmailSave} style={btnStyle('#10B981')}>Salva</button>
                <button onClick={() => { setEditingEmail(false); setNewEmail(user.email); }} style={btnStyle()}>Annulla</button>
              </div>
            ) : (
              <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>
                {user.email}
                <button onClick={() => setEditingEmail(true)} style={{ ...btnStyle(), marginLeft: '8px', padding: '3px 8px' }}>
                  <Mail size={13} /> Modifica
                </button>
              </h2>
            )}
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px', fontSize: '12px', color: '#6c6c6d' }}>
              <span>ID: <code style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px' }}>{user.user_id}</code></span>
              {user.created_at && <span>Creato: {new Date(user.created_at).toLocaleDateString('it-IT')}</span>}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <span style={{
              padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
              background: user.is_active ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
              color: user.is_active ? '#10B981' : '#EF4444',
            }}>
              {user.is_active ? 'Attivo' : 'Inattivo'}
            </span>
            {user.is_superadmin && (
              <span style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, background: 'rgba(232,0,45,0.08)', color: '#E8002D' }}>
                Superadmin
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={() => setShowResetPw(true)} style={btnStyle()}>
            <KeyRound size={13} /> Reset password
          </button>
          <button
            onClick={() => setConfirmAction({
              title: user.is_active ? 'Disattiva utente' : 'Attiva utente',
              message: user.is_active
                ? `Disattivare ${user.email}? Non potrà più accedere.`
                : `Riattivare ${user.email}?`,
              onConfirm: handleToggleActive,
              variant: user.is_active ? 'danger' : 'default',
            })}
            style={btnStyle(user.is_active ? '#EF4444' : '#10B981')}
          >
            {user.is_active ? 'Disattiva' : 'Attiva'}
          </button>
          <button
            onClick={() => setConfirmAction({
              title: user.is_superadmin ? 'Revoca superadmin' : 'Promuovi a superadmin',
              message: user.is_superadmin
                ? `Revocare i privilegi superadmin di ${user.email}?`
                : `Promuovere ${user.email} a superadmin?`,
              onConfirm: handleToggleSuperadmin,
              variant: user.is_superadmin ? 'danger' : 'default',
            })}
            style={btnStyle(user.is_superadmin ? '#EF4444' : '#E8002D')}
          >
            {user.is_superadmin ? <ShieldOff size={13} /> : <Shield size={13} />}
            {user.is_superadmin ? 'Revoca SA' : 'Promuovi SA'}
          </button>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0 }}>Tenant</h3>
          <button onClick={() => setShowAssignTenant(true)} style={btnStyle('#E8002D')}>
            <UserPlus size={13} /> Assegna a tenant
          </button>
        </div>
        <AdminTable
          columns={tenantColumns}
          rows={userTenants}
          emptyMessage="Nessun tenant assegnato"
        />
      </div>

      <ResetPasswordModal open={showResetPw} onClose={() => setShowResetPw(false)} userId={userId} />
      <AssignTenantModal
        open={showAssignTenant}
        onClose={() => setShowAssignTenant(false)}
        userEmail={user.email}
        availableTenants={availableTenants}
        onAssigned={loadUser}
      />
      {confirmAction && (
        <ConfirmDialog
          open={true}
          onClose={() => setConfirmAction(null)}
          onConfirm={confirmAction.onConfirm}
          title={confirmAction.title}
          message={confirmAction.message}
          variant={confirmAction.variant}
        />
      )}
    </div>
  );
}
